import type { DeviceEventKind } from '@konvoi/shared';

/** Eine Gerätemeldung, die noch zum Server muss. */
export interface Geraetemeldung {
  readonly kind: DeviceEventKind;
  readonly at: number;
}

/**
 * Was das Gerät über seine eigene Verbindung weiß — mitgeschrieben, nicht
 * befolgt (A-139).
 *
 * ## Wozu
 *
 * Die App erkennt ein Funkloch am Herzschlag, und das dauert bis zu einer
 * Minute. Auf der Fahrt 208079 am 18.09.2026 zeigte das Telefon oben längst
 * „kein Netz", während die App noch grün war. Ob der Browser das Funkloch
 * früher **meldet**, und wie viel früher, wusste niemand. Das hier schreibt es
 * mit: wann das Gerät „offline" und „online" meldet, und wann der Herzschlag
 * aufgibt — der Vergleichswert.
 *
 * Befolgt wird davon nichts. Die Verbindung verhält sich genau wie vorher. Am
 * iPhone hat die Fahrt 194223 am 21.09.2026 die Frage beantwortet: In zwei
 * Funklöchern meldete iOS kein `offline`, auch bei „SOS" in der Statusleiste
 * nicht — im Flugmodus sofort. Für andere Geräte ist sie offen.
 *
 * ## Was hier nicht geschieht
 *
 * Gefiltert wird nichts. Meldet der Browser zweimal „offline", stehen zwei
 * Zeilen im Protokoll — eine Messung soll aufschreiben, was geschah, nicht was
 * vernünftig wäre. Nur die Anzeige, seit wann das Gerät offline ist, hält sich
 * an die erste Meldung.
 */
export class Geraetemeldungen {
  #offen: Geraetemeldung[] = [];
  #offlineSeit: number | null = null;

  /** Etwas ist geschehen. */
  erfasse(kind: DeviceEventKind, at: number): void {
    if (kind === 'network_offline' && this.#offlineSeit === null) {
      this.#offlineSeit = at;
    }
    if (kind === 'network_online') {
      this.#offlineSeit = null;
    }
    this.#offen.push({ kind, at });
  }

  /** Seit wann das Gerät „offline" meldet — oder `null`, wenn es das nicht tut. */
  get offlineSeit(): number | null {
    return this.#offlineSeit;
  }

  /** Alles, was noch zum Server muss, in der Reihenfolge des Geschehens. Danach ist die Liste leer. */
  nimmAlle(): Geraetemeldung[] {
    const alle = this.#offen;
    this.#offen = [];
    return alle;
  }

  /**
   * Was nicht hinausging oder nicht bestätigt ist, kommt zurück.
   *
   * Geordnet nach dem Zeitpunkt des Geschehens: Eine Meldung, die in eine tote
   * Leitung ging, ist älter als alles, was seither dazukam, und soll im
   * Protokoll auch davor stehen.
   */
  zurueck(meldungen: readonly Geraetemeldung[]): void {
    if (meldungen.length === 0) {
      return;
    }
    this.#offen = [...meldungen, ...this.#offen].sort((a, b) => a.at - b.at);
  }
}
